import type { QueryConstraint, DocumentData, WithFieldValue } from 'firebase/firestore'
import { firestoreClient } from './firestoreClient'
import { firestoreTransformer } from './firestoreTransformer'
import { firestoreErrorHandler } from './firestoreErrorHandler'
import type { FirestoreDocument } from './firestoreTypes'

export type { FirestoreDocument } from './firestoreTypes'
export { FirestoreError } from './firestoreTypes'

export { where, orderBy, limit, serverTimestamp, Timestamp } from 'firebase/firestore'

export async function getDocument<T extends FirestoreDocument>(
  collectionName: string,
  documentId: string
): Promise<T | null> {
  try {
    return await firestoreClient.getDocument<T>(collectionName, documentId)
  } catch (error) {
    return firestoreErrorHandler.handleGetDocumentError(collectionName, documentId, error)
  }
}

/** Lectura de un documento SIEMPRE del servidor (ver `FirestoreClient.getDocumentFromServer`). */
export async function getDocumentFromServer<T extends FirestoreDocument>(
  collectionName: string,
  documentId: string
): Promise<T | null> {
  try {
    return await firestoreClient.getDocumentFromServer<T>(collectionName, documentId)
  } catch (error) {
    return firestoreErrorHandler.handleGetDocumentError(collectionName, documentId, error)
  }
}

export async function getDocuments<T extends FirestoreDocument>(
  collectionName: string,
  constraints: QueryConstraint[] = []
): Promise<T[]> {
  try {
    return await firestoreClient.getDocuments<T>(collectionName, constraints)
  } catch (error) {
    return firestoreErrorHandler.handleGetDocumentsError(collectionName, error)
  }
}

export async function getSubcollectionDocuments<T extends FirestoreDocument>(
  parentCollection: string,
  parentId: string,
  subcollectionName: string,
  constraints: QueryConstraint[] = []
): Promise<T[]> {
  try {
    return await firestoreClient.getSubcollectionDocuments<T>(
      parentCollection,
      parentId,
      subcollectionName,
      constraints
    )
  } catch (error) {
    return firestoreErrorHandler.handleGetDocumentsError(
      `${parentCollection}/${parentId}/${subcollectionName}`,
      error
    )
  }
}

export async function documentExists(
  collectionName: string,
  documentId: string
): Promise<boolean> {
  try {
    return await firestoreClient.documentExists(collectionName, documentId)
  } catch (error) {
    return firestoreErrorHandler.handleDocumentExistsError(collectionName, documentId, error)
  }
}

export async function createDocument<T extends DocumentData>(
  collectionName: string,
  data: WithFieldValue<T>
): Promise<string> {
  try {
    const preparedData = firestoreTransformer.prepareForCreate(data as Record<string, unknown>)
    return await firestoreClient.createDocument(collectionName, preparedData)
  } catch (error) {
    return firestoreErrorHandler.handleCreateDocumentError(collectionName, error)
  }
}

export async function setDocument<T extends DocumentData>(
  collectionName: string,
  documentId: string,
  data: WithFieldValue<T>,
  merge = false
): Promise<void> {
  try {
    const cleanedData = firestoreTransformer.removeUndefinedFields(
      data as Record<string, unknown>
    )
    const dataWithTimestamp = firestoreTransformer.addTimestamps(
      cleanedData as Record<string, unknown>,
      merge
    )
    await firestoreClient.setDocument(collectionName, documentId, dataWithTimestamp, merge)
  } catch (error) {
    firestoreErrorHandler.handleSetDocumentError(collectionName, documentId, error)
  }
}

export async function updateDocument<T extends Partial<DocumentData>>(
  collectionName: string,
  documentId: string,
  data: WithFieldValue<T>
): Promise<void> {
  try {
    const preparedData = firestoreTransformer.prepareForUpdate(data as Record<string, unknown>)
    await firestoreClient.updateDocument(collectionName, documentId, preparedData)
  } catch (error) {
    firestoreErrorHandler.handleUpdateDocumentError(collectionName, documentId, error)
  }
}

export async function deleteDocument(
  collectionName: string,
  documentId: string
): Promise<void> {
  try {
    await firestoreClient.deleteDocument(collectionName, documentId)
  } catch (error) {
    firestoreErrorHandler.handleDeleteDocumentError(collectionName, documentId, error)
  }
}

export { firestoreClient, firestoreTransformer, firestoreErrorHandler }
